import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Breadcrumb from '../../../component/common/Breadcrumb';
import WorkoutDetailsTab from '../../../component/HealthComponent/WorkoutForm/WorkoutDetailsTab';
import StretchesTab from '../../../component/HealthComponent/WorkoutForm/StretchesTab';
import ExercisesTab from '../../../component/HealthComponent/WorkoutForm/ExercisesTab';
import ViewWorkout from './viewWorkout';
import WorkoutSuccessModal from '../../../component/HealthComponent/WorkoutSuccessModal';
import {
  useWorkoutCreation,
  WorkoutCreationProvider
} from '../../../context/WorkoutCreationContext';
import { useHealthLoading } from '../../../context/HealthLoadingContext';
import { getLocalStorageItem, errorToast } from '../../../utils/helper';
import { getWorkoutById } from '../../../api/healthAPI';

const NewWorkoutComponent = () => {
  const location = useLocation();
  const propsData = location?.state;
  const id = new URLSearchParams(window.location.search).get('id');
  const userData = getLocalStorageItem('userData') && JSON.parse(getLocalStorageItem('userData'));

  // Use workout creation context
  const { errors, updateWorkoutData, currentStep, workoutId, submitWorkout } = useWorkoutCreation();
  const { withLoading } = useHealthLoading();

  // Tab management
  const [isView, setIsView] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handlePublish = async () => {
    await withLoading(async () => {
      try {
        const result = await submitWorkout();
        if (result?.success) {
          setIsPreview(false);
          setShowSuccessModal(true);
        } else {
          throw new Error(result?.error || 'Failed to publish workout');
        }
      } catch (err) {
        errorToast(err.message || 'Failed to publish workout');
      }
    });
  };

  // Stretches and exercises are managed by context now

  // Breadcrumb
  const [pages] = useState([
    {
      name: 'Health Dashboard',
      href: `${userData?.slug ? `/${userData.slug}` : ''}/health/dashboard`
    },
    { name: 'Workouts', href: `${userData?.slug ? `/${userData.slug}` : ''}/health/workouts` },
    { name: id ? 'Edit Workout' : 'New Workout', current: true }
  ]);

  // Stretches and exercises are handled within tab components via context

  // Fetch workout data for editing
  const fetchWorkoutData = async (workoutId) => {
    try {
      const response = await getWorkoutById(workoutId);

      if (response?.meta?.code === 1 && response?.data) {
        const workoutData = response.data;

        // Update context with fetched data
        updateWorkoutData({
          workoutId: workoutData._id,
          workout_name: workoutData.workout_name || '',
          workout_overview: workoutData.workout_overview || '',
          location: workoutData.location || '',
          difficulty: workoutData.difficulty || '',
          category_ids: (workoutData.category_ids || []).map(
            (category) => category?._id || category
          ),
          muscle_groups: workoutData.muscle_groups || [],
          thumbnail_url: workoutData.cover_image_url || '',
          currentStep: Math.min(workoutData.current_step + 1 || 1, 3)
        });

        // Set exercises if available (store in context)
        if (workoutData.exercises) {
          const mappedExercises = workoutData.exercises.map((ex, index) => {
            const exObj = ex.exercise_id || {};
            const exerciseId = exObj._id || exObj.id || ex.exercise_id || ex._id || ex.id;
            return {
              exercise_id: exerciseId,
              sequence: ex.sequence || index + 1,
              metadata: ex.metadata || { sets: [] },
              name: exObj.name || '',
              muscle_groups: exObj.muscle_groups || [],
              category_ids: exObj.category_ids || [],
              exercise_duration: exObj.exercise_duration || '',
              rest_time: exObj.rest_time || 10,
              video_url: exObj.video_url || '',
              image_url: exObj.image_url || '',
              difficulty: exObj.difficulty || '',
              type: 'exercise'
            };
          });
          updateWorkoutData({ exercises: mappedExercises });
        }

        // Set stretches if available (store in context)
        if (workoutData.stretches) {
          const mappedStretches = workoutData.stretches.map((st, index) => {
            // API provides: { exercise_id: { _id, ...full exercise fields }, sequence, metadata }
            const exObj = st.exercise_id || {};
            const exerciseId = exObj._id || exObj.id || st.exercise_id || st.stretch_id || st._id;
            return {
              // minimal identifiers used by updateWorkout payload
              exercise_id: exerciseId,
              sequence: st.sequence || index + 1,
              metadata: st.metadata || { sets: [] },
              // populate display/form fields for SingleExerciseForm auto-fill
              name: exObj.name || '',
              muscle_groups: exObj.muscle_groups || [],
              category_ids: exObj.category_ids || [],
              exercise_duration: exObj.exercise_duration || '',
              restTime: exObj.rest_time || 10,
              video_url: exObj.video_url || '',
              image_url: exObj.image_url || '',
              difficulty: exObj.difficulty || '',
              type: 'stretch'
            };
          });
          updateWorkoutData({ stretches: mappedStretches });
        }
      }
    } catch (err) {
      console.error('Error fetching workout data:', err);
      errorToast('Failed to load workout data');
    }
  };

  // Load data if editing
  useEffect(() => {
    const workoutId = id || propsData?._id;
    if (workoutId) {
      if (propsData?.action === 'view') {
        setIsView(true);
      }
      fetchWorkoutData(workoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, propsData]);

  // If in preview mode, show ViewWorkout component
  if (isPreview) {
    return (
      <>
        <ViewWorkout
          isPreview={true}
          onExitPreview={() => setIsPreview(false)}
          onPublish={handlePublish}
        />
        <WorkoutSuccessModal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} />
      </>
    );
  }

  return (
    <div className="relative lg:mb-[45px]">
      <Helmet>
        <meta charSet="utf-8" />
        <title>{id ? 'Edit Workout' : 'New Workout'} | Shoorah Admin</title>
      </Helmet>

      <Breadcrumb pageList={pages} className="lg:mt-8" />

      {/* Tab Navigation with Preview Button */}
      <div className="flex items-center justify-between mt-6">
        <nav className="rounded-t-lg" role="tablist" aria-label="Workout tabs">
          <ul className="flex gap-6">
            <li>
              <button
                type="button"
                role="tab"
                aria-selected={currentStep === 1}
                tabIndex={currentStep === 1 ? 0 : -1}
                onClick={() => {
                  if (workoutId || currentStep >= 3) {
                    updateWorkoutData({ currentStep: 1 });
                  }
                }}
                className={`px-1 pb-3 pt-4 text-base ${
                  currentStep === 1 ? 'text-black font-medium' : 'text-gray-400 dark:text-gray-500'
                } ${workoutId || currentStep >= 3 ? 'cursor-pointer' : 'cursor-default'}`}
                disabled={!workoutId && currentStep < 3 && currentStep !== 1}
              >
                Details
              </button>
            </li>
            <li>
              <button
                type="button"
                role="tab"
                aria-selected={currentStep === 2}
                tabIndex={currentStep === 2 ? 0 : -1}
                onClick={() => {
                  if (workoutId || currentStep >= 2) {
                    updateWorkoutData({ currentStep: 2 });
                  }
                }}
                className={`px-1 pb-3 pt-4 text-base ${
                  currentStep === 2 ? 'text-black font-medium' : 'text-gray-400 dark:text-gray-500'
                } ${workoutId || currentStep >= 2 ? 'cursor-pointer' : 'cursor-default'}`}
                disabled={!workoutId && currentStep < 2}
              >
                Stretches
              </button>
            </li>
            <li>
              <button
                type="button"
                role="tab"
                aria-selected={currentStep === 3}
                tabIndex={currentStep === 3 ? 0 : -1}
                onClick={() => {
                  if (workoutId || currentStep >= 3) {
                    updateWorkoutData({ currentStep: 3 });
                  }
                }}
                className={`px-1 pb-3 pt-4 text-base ${
                  currentStep === 3 ? 'text-black font-medium' : 'text-gray-400 dark:text-gray-500'
                } ${workoutId || currentStep >= 3 ? 'cursor-pointer' : 'cursor-default'}`}
                disabled={!workoutId && currentStep < 3}
              >
                Exercises
              </button>
            </li>
          </ul>
        </nav>

        {/* Preview Button - Visible on all steps */}
        {!isView && (
          <button
            type="button"
            onClick={() => setIsPreview(true)}
            className="px-6 py-2 border border-gray-300 bg-white text-gray-700 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            Preview
          </button>
        )}
      </div>

      {/* Tab Content */}
      <div className="mt-6 space-y-6">
        {currentStep === 1 && (
          <div role="tabpanel" aria-labelledby="details-tab">
            <WorkoutDetailsTab isView={isView} errors={errors} />
          </div>
        )}

        {currentStep === 2 && (
          <div role="tabpanel" aria-labelledby="stretches-tab">
            <StretchesTab error={errors} isView={isView} />
          </div>
        )}

        {currentStep === 3 && (
          <div role="tabpanel" aria-labelledby="exercises-tab">
            <ExercisesTab
              error={errors}
              isView={isView}
              onPreviewAndPublish={() => setIsPreview(true)}
            />
          </div>
        )}
      </div>
      <WorkoutSuccessModal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} />
    </div>
  );
};

// Wrap the component with the WorkoutCreationProvider
const NewWorkout = () => {
  return (
    <WorkoutCreationProvider>
      <NewWorkoutComponent />
    </WorkoutCreationProvider>
  );
};

export default NewWorkout;
